import React from "react";

import styled from "styled-components/macro";

import { faPencil } from "@fortawesome/pro-solid-svg-icons";
import API from "Signups/API";
import { signupItemStateHooks } from "Signups/SignupView/SignupSheet/Screens/SignupItems/stateHooks";
import { useSignups } from "Signups/state";

import FnrLoadingBricks from "UI/FnrLoadingBricks";
import { FnrPopover } from "UI/FnrPopover2";

import HoverButton from "./HoverButton";

export default function EditNumberOfSlotsButton({
  isHovering,
  showNumSlotsPopover,
  setShowNumSlotsPopover,
  signupItemID,
  user,
  paidSlotsCount,
  setShowDeletePaidSignupModal,
  setUpdatedNumberOfSlots,
  setRemovalType,
}) {
  const [state, dispatch] = useSignups();
  const triggerRef = React.useRef();
  const [loadingNum, setLoadingNum] = React.useState(null);
  const { getSignupItem } = signupItemStateHooks.useGetSignupItem();

  const currentNumberOfSlots = user.numberOfSlots;
  const signupItem = state.currentSignupItem;

  const costType = signupItem.costType;

  const maxSlotsToDisplay = calculateMaxSlotsToDisplay(
    signupItem,
    currentNumberOfSlots
  );

  const options = Array.from({ length: maxSlotsToDisplay }, (v, k) => k + 1);

  const handlePopoverTriggerClick = () => {
    setShowNumSlotsPopover(!showNumSlotsPopover);
  };

  const handleOptionClick = (num) => {
    if (loadingNum) return;

    setLoadingNum(num);

    if (costType === "PerSlot" && num < paidSlotsCount) {
      setRemovalType("slot");
      setUpdatedNumberOfSlots(num);
      setShowDeletePaidSignupModal(true);
      setLoadingNum(null);
      setShowNumSlotsPopover(false);
    } else {
      API.updateUserSignupSlots({
        signupListID: state.currentSignupList.ID,
        signupItemID,
        userID: user.userID,
        numberOfSlots: num,
        signedUpViaID: 5, // 5 indicates updated by an admin
      })
        .post()
        .then((resp) => {
          getSignupItem(signupItemID);
          dispatch({
            type: "Other User Signup Updated",
            signupList: resp.signupList,
            allSignupLists: resp.allSignupLists,
          });
          setLoadingNum(null);
          setShowNumSlotsPopover(false);
        })
        .catch((err) => {
          console.log("Error:", err);
          setLoadingNum(null);
          setShowNumSlotsPopover(false);
          alert(
            "Uh oh! There was a problem updating the number of slots. Please refresh the page and try again."
          );
        });
    }
  };

  return (
    <Wrapper ref={triggerRef}>
      <HoverButton
        icon={faPencil}
        isHovering={isHovering || showNumSlotsPopover}
        isActive={showNumSlotsPopover}
        onClick={handlePopoverTriggerClick}
      />

      <FnrPopover
        show={showNumSlotsPopover}
        buttonRef={triggerRef}
        onShouldClose={() => {
          setShowNumSlotsPopover(false);
          setLoadingNum(null);
        }}
      >
        {maxSlotsToDisplay < 2 ? (
          <ItemFull>
            This item is full! To increase the number of slots for this user,
            please update the number of available slots for this Signup item.
          </ItemFull>
        ) : (
          <>
            <Title>Number of Slots:</Title>
            <OptionsList>
              {options.map((num) => (
                <Option
                  key={num}
                  role="button"
                  onClick={() => handleOptionClick(num)}
                  isSelected={num === currentNumberOfSlots}
                >
                  {num === loadingNum ? (
                    <FnrLoadingBricks show color="gray" size={16} />
                  ) : (
                    num
                  )}
                </Option>
              ))}
            </OptionsList>
          </>
        )}
      </FnrPopover>
    </Wrapper>
  );
}

const Wrapper = styled.div``;

const Title = styled.div`
  text-align: center;
  opacity: 0.7;
  font-size: 14px;
  margin-bottom: 3px;
`;

const OptionsList = styled.div`
  max-height: 300px;
  overflow-y: auto;
`;

const Option = styled.div`
  position: relative;
  padding: 3px;
  font-size: 19px;
  text-align: center;
  border-radius: 6px;
  background: ${({ isSelected }) =>
    isSelected ? "rgba(255, 255, 255, 0.2)" : "none"};
  transition: background 250ms;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  &:active {
    transform: scale(0.97);
  }
`;

const ItemFull = styled.div`
  padding: 3px;
  font-size: 14px;
  max-width: 240px;
`;

function calculateMaxSlotsToDisplay(signupItem, currentNumberOfSlots) {
  // should be the user's currentNumberOfSlots + remaining available slots
  // but capped at maxSingleUserSlots setting

  const { maxSingleUserSlots, slotsTaken, totalSlots } = signupItem;
  const remainingItemSlotsAvailable = totalSlots - slotsTaken;
  const uncappedSlotsToShow =
    currentNumberOfSlots + remainingItemSlotsAvailable;

  if (uncappedSlotsToShow < maxSingleUserSlots) {
    return uncappedSlotsToShow;
  } else {
    return maxSingleUserSlots;
  }
}
